import {useQuery} from '@tanstack/react-query';
import type {WorkstreamBasicWithSummary} from 'client';
import {WorkstreamListService} from 'client';
import {useMemo} from 'react';
import {useWorkstreams} from 'views/workstreams/detail/hooks';
import type {AltitudeFilterValue, WorkstreamFilters} from 'views/workstreams/detail/types';

/**
 * /workstreams: what an empty My workstreams shows instead — your
 * closest team with workstreams in `timeframes` (your own, then up toward your
 * org, each counting its sub-teams'), and those workstreams' cards. `team` is
 * null when nothing up to your org has any, or the server lacks the endpoint.
 * Only asks once `enabled` (My is known to be empty).
 */
export const useNearestTeamWorkstreams = ({
  timeframes,
  health,
  enabled,
}: {
  timeframes: NonNullable<WorkstreamFilters['timeframes']>;
  health?: WorkstreamFilters['health'];
  enabled: boolean;
}) => {
  const filtersParam = JSON.stringify({timeframes});
  const nearest = useQuery({
    queryKey: ['prototype-nearest-team', filtersParam],
    queryFn: () =>
      WorkstreamListService.getPrototypeNearestTeamApiV1WorkstreamPrototypeNearestTeamGet(
        filtersParam
      ),
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const team = nearest.data?.team ?? null;
  const ids = nearest.data?.workstream_ids;

  const filters: WorkstreamFilters = useMemo(
    () => ({
      altitude: ['false'] as AltitudeFilterValue[],
      timeframes,
      workstreams: ids ?? [],
      ...(health && health.length > 0 ? {health} : {}),
    }),
    [timeframes, ids, health]
  );
  const cards = useWorkstreams({
    scope_type: 'company',
    scope_value: null,
    filters,
    group_by: 'none',
    autoFetchAllPages: true,
    enabled: enabled && team !== null && (ids?.length ?? 0) > 0,
  });

  const cardRows = cards.data?.workstreams;
  const rows = useMemo(
    () => (team ? ((cardRows ?? []) as WorkstreamBasicWithSummary[]) : []),
    [team, cardRows]
  );

  return {
    team,
    rows,
    // Settled once the team is known and, if there is one, its cards.
    isLoading:
      enabled && (nearest.isPending || (team !== null && !cards.hasFirstPageForCurrentSignature)),
  };
};
